/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';
import DeliveryProgressBar, { IProgressBarStep } from '../../../components/delivery-progress-bar';
import { dateLocaleFormatter, toLocalDate } from '../../../utilities';
import { DeliverySummaryProps, DisplayItemProps } from '../lxs-anonymous-delivery.data';

const SummaryItem: React.FC<DisplayItemProps> = ({ CLASS_NAME, label, value }) => (
    <div className={`${CLASS_NAME}__container-main-summary-item`}>
        <span className={`${CLASS_NAME}__container-main-summary-label`}>{label}</span>
        <h2 className={`${CLASS_NAME}__container-main-summary-value`}>{value}</h2>
    </div>
);

const toProgressBarSteps = (steps: DeliverySummaryProps['order']['steps']): IProgressBarStep[] =>
    steps.map((step, index) => ({
        key: `${index}-${step.stepKey}`,
        eventName: step.stepKey,
        icon: step.icon,
        status: step.status,
        timestamp: step.timestamp,
        location: step.location
    }));

const DeliverySummary: React.FC<DeliverySummaryProps> = ({ CLASS_NAME, order, resources, localizationState }) => (
    <div className={`${CLASS_NAME}__container-main`}>
        <div className={`${CLASS_NAME}__container-main-summary`}>
            <SummaryItem CLASS_NAME={CLASS_NAME} label={resources.lxsAnonymousDeliveryDeliveryNumberLabel} value={order.deliveryNumber} />
            <SummaryItem CLASS_NAME={CLASS_NAME} label={resources.lxsAnonymousDeliveryCustomerPOLabel} value={order.customerPO} />
            <SummaryItem
                CLASS_NAME={CLASS_NAME}
                label={resources.lxsAnonymousDeliveryEstimatedArrivalLabel}
                value={dateLocaleFormatter(localizationState).formatDate(toLocalDate(order.estimatedArrivalDate))}
            />
        </div>

        <DeliveryProgressBar
            CLASS_NAME={CLASS_NAME}
            steps={toProgressBarSteps(order.steps)}
            resources={resources}
            localizationState={localizationState}
        />

        <div className={`${CLASS_NAME}__container-main-tracking-row`}>
            {order.liveTrackingAvailable && order.liveTrackingUrl ? (
                <a
                    className={`${CLASS_NAME}__container-main-tracking-link`}
                    href={order.liveTrackingUrl}
                    target='_blank'
                    rel='noreferrer'
                    aria-label={resources.lxsAnonymousDeliveryLiveTrackingAvailableLabel}
                >
                    {resources.lxsAnonymousDeliveryLiveTrackingAvailableLabel}
                </a>
            ) : (
                <span className={`${CLASS_NAME}__container-main-tracking-label`}>{resources.lxsAnonymousDeliveryNoLiveTrackingLabel}</span>
            )}
        </div>
    </div>
);

export default DeliverySummary;
